/**
 * Creator portal.
 *
 * Routes the unauthenticated journey (sign-up, sign-in, verification,
 * recovery) and the authenticated shell. Home shows profile, earnings and
 * every open campaign; balances stay per currency because there is no
 * combined total across currencies.
 *
 * The first sign-in stays on the profile, then one connected channel, then
 * staff approval. Campaigns stay locked until that approval.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button, StateBlock } from "@ui/primitives";
import { PortalShell, type NavItem } from "@app/shared/PortalShell";
import { CreatorAvatar, Icons } from "@app/shared/icons";
import { useRoute } from "@app/shared/useRoute";
import { api, getSession, type SessionResponse } from "@app/shared/api";
import { ToastHost } from "@app/shared/Toasts";
import {
  SignUpScreen, LoginScreen, VerifyEmailScreen, ForgotPasswordScreen,
  ResetPasswordScreen, VerifyBanner,
} from "@app/shared/AuthScreens";
import { CampaignDetailScreen } from "./Discover";
import { WorkScreen, WorkDetailScreen } from "./Work";
import { PageSkeleton } from "@app/shared/Skeletons";
import { SubmissionDetailScreen } from "./Submissions";
import { EarningsScreen, PerformanceScreen } from "./Earnings";
import { PayoutsScreen } from "./Payouts";
import { Home } from "./Home";
import { SocialAccounts } from "./Social";
import { ProfileScreen } from "./Profile";
import {
  AwaitingBanner, AwaitingVerification, ChannelStep, ProfileStep,
  creatorNav, keepSearch, setupPath, useCreatorAccess,
  type CreatorAccess, type CreatorAccessStep,
} from "./AccessGate";

const NAV: readonly NavItem[] = [
  { label: "Home", href: "/", icon: Icons.home, tab: true },
  { label: "My Campaigns", href: "/work", icon: Icons.work, shortLabel: "Campaigns", tab: true },
  { label: "Performance", href: "/performance", icon: Icons.performance },
  { label: "Earnings", href: "/earnings", icon: Icons.earnings },
  { label: "Payouts", href: "/payouts", icon: Icons.payouts },
  { label: "Social accounts", href: "/social", icon: Icons.social },
  { label: "Profile", href: "/onboarding", icon: Icons.profile },
];

/* ------------------------------------------------------------------ app */

export function App() {
  const [route, navigate] = useRoute();
  const [session, setSession] = useState<SessionResponse | null>(null);
  const { access, reload } = useCreatorAccess(session?.authenticated === true);
  const stepRef = useRef<CreatorAccessStep | null>(null);
  const accessStep: CreatorAccessStep | null =
    access && access !== "error" ? access.step : null;
  stepRef.current = accessStep;

  const refresh = useCallback(async () => {
    const r = await getSession();
    setSession(r.ok ? r.data : { authenticated: false, audience: "creator" });
  }, []);

  const onConnections = useCallback((active: number) => {
    const step = stepRef.current;
    if (active >= 1 && step === "channels") {
      void reload().then((next) => {
        if (next?.step === "pending") navigate(keepSearch("/"));
      });
    } else if (active < 1 && step === "pending") {
      void reload();
    }
  }, [reload, navigate]);

  useEffect(() => { void refresh(); }, [refresh]);

  const path = route.path;
  useEffect(() => {
    if (!session?.authenticated) return;
    if (path === "/verify-email" || path === "/reset-password") return;
    const step = access && access !== "error" ? access.step : null;
    if (step && step !== "ready" && step !== "blocked" && step !== "pending") {
      const onProfile = path === "/onboarding";
      const onSocial = path === "/social";
      if ((step === "email" || step === "profile") && onProfile) return;
      if (step === "channels" && (onSocial || onProfile)) return;
      navigate(keepSearch(step === "channels" ? "/social" : "/onboarding"));
      return;
    }
    if (!step || step === "ready") {
      if (path === "/discover") navigate(keepSearch("/"));
      if (path === "/submissions" || path === "/submissions/new") navigate(keepSearch("/work"));
    }
  }, [session, access, path, navigate]);
  const token = route.query.get("token");

  if (path === "/verify-email") {
    return <VerifyEmailScreen token={token} onDone={() => { void refresh(); navigate("/"); }} />;
  }
  if (path === "/reset-password") {
    return <ResetPasswordScreen token={token} onNavigate={navigate} />;
  }

  if (session === null) {
    return (
      <div className="cm-rail-offset" style={{
        minHeight: "100vh", background: "var(--canvas)",
        padding: "40px 36px 64px",
      }}>
        <PageSkeleton />
      </div>
    );
  }

  if (!session.authenticated) {
    if (path === "/signup") return <SignUpScreen audience="creator" onNavigate={navigate} />;
    if (path === "/forgot-password") return <ForgotPasswordScreen onNavigate={navigate} />;
    return (
      <LoginScreen audience="creator" onNavigate={navigate}
        onSignedIn={() => { void refresh(); navigate("/"); }} />
    );
  }

  const signedIn = session;

  async function signOut() {
    await api.post("/auth/logout");
    await refresh();
    navigate(keepSearch("/"));
  }

  async function onProfileSaved() {
    await refresh();
    const next = await reload();
    if (next?.step === "channels") navigate(keepSearch("/social"));
    else if (next?.step === "pending") navigate(keepSearch("/"));
  }

  // `/campaigns/:id` is a real URL a creator can be linked to, so the id
  // comes off the path rather than out of component state.
  const campaignId = path.startsWith("/campaigns/") ? path.slice("/campaigns/".length) : null;
  const workId = path.startsWith("/work/") ? path.slice("/work/".length) : null;
  // `/submissions/:id` is a real URL a creator is linked to from a review
  // notification, so the id comes off the path rather than component state.
  const submissionId = path.startsWith("/submissions/") && path !== "/submissions/new"
    ? path.slice("/submissions/".length) : null;
  const showingLock = accessStep !== null && accessStep !== "ready"
    && !setupPath(path, accessStep);
  const activeHref = showingLock ? ""
    : campaignId || path === "/" || path === "/discover" ? "/"
    : path === "/work" || workId || path.startsWith("/submissions") ? "/work"
    : path === "/earnings" ? "/earnings"
    : path === "/payouts" ? "/payouts"
    : path === "/performance" ? "/performance"
    : path === "/social" ? "/social"
    : path === "/onboarding" ? "/onboarding"
    : "/";

  return (
    <ToastHost>
    <PortalShell
      audience="Creator"
      nav={creatorNav(NAV, accessStep)}
      activeHref={activeHref}
      variant="rail"
      headerEnd={
        <a href={keepSearch("/onboarding")} aria-label="Your profile" title="Your profile"
          style={{ display: "grid", textDecoration: "none" }}>
          <HeaderAvatar />
        </a>
      }
    >
      {gatedBody()}
    </PortalShell>
    </ToastHost>
  );

  function gatedBody(): ReactNode {
    if (access === null) return <PageSkeleton />;
    if (access === "error") {
      return (
        <StateBlock
          kind="error"
          title="Could not check your account"
          detail="Campaigns stay closed until we can confirm your profile. Refresh the page."
          action={<Button variant="secondary" onClick={() => void signOut()}>Sign out</Button>}
        />
      );
    }
    if (access.step === "blocked") {
      return (
        <>
          <StateBlock
            kind="forbidden"
            title="This account cannot continue"
            detail="This account is suspended or closed."
          />
          <div>
            <Button size="sm" variant="ghost" onClick={() => void signOut()}>Sign out</Button>
          </div>
        </>
      );
    }
    if (access.step !== "ready" && !setupPath(path, access.step)) {
      return access.step === "pending" ? <AwaitingVerification /> : <PageSkeleton />;
    }
    if (access.step === "email" || access.step === "profile") {
      return (
        <ProfileStep
          step={access.step}
          email={signedIn.user?.email}
          onSaved={() => void onProfileSaved()}
          onSignOut={() => void signOut()}
        />
      );
    }
    if (access.step === "channels" && path === "/social") {
      return (
        <ChannelStep
          onConnections={onConnections}
          onSignOut={() => void signOut()}
        />
      );
    }
    if (access.step === "channels" && path === "/onboarding") {
      return (
        <ProfileStep
          step="channels"
          email={signedIn.user?.email}
          onSaved={() => void onProfileSaved()}
          onSignOut={() => void signOut()}
        />
      );
    }
    return portalBody(access);
  }

  function portalBody(current: CreatorAccess): ReactNode {
    const pending = current.step === "pending";
    if (campaignId && pending) return <AwaitingVerification />;
    return (
      <>
      {pending && (path === "/onboarding" || path === "/social") ? <AwaitingBanner /> : null}
      {campaignId ? (
        <CampaignDetailScreen
          id={campaignId}
          onBack={() => navigate("/")}
          onJoined={() => navigate(`/work/${campaignId}`)}
        />
      ) : path === "/earnings" ? (
        <EarningsScreen />
      ) : path === "/payouts" ? (
        <PayoutsScreen />
      ) : path === "/performance" ? (
        <PerformanceScreen />
      ) : workId ? (
        <WorkDetailScreen
          id={workId}
          onBack={() => navigate("/work")}
          tab={route.query.get("tab")}
          onTab={(t) => navigate(
            t === "submissions" ? `/work/${workId}?tab=submissions`
            : t === "brief" ? `/work/${workId}?tab=brief`
            : `/work/${workId}`,
          )}
          onOpenSubmission={(sid) => navigate(`/submissions/${sid}`)}
        />
      ) : path === "/work" ? (
        <WorkScreen onOpenCampaign={(id) => navigate(`/work/${id}`)} />
      ) : submissionId ? (
        <SubmissionDetailScreen
          id={submissionId}
          onBack={(campaignId) => navigate(
            campaignId ? `/work/${campaignId}?tab=submissions` : "/work",
          )}
        />
      ) : path === "/submissions" || path === "/submissions/new" ? (
        <PageSkeleton />
      ) : path === "/social" ? (
        <SocialAccounts {...(pending ? { onConnections } : {})} />
      ) : path === "/onboarding" ? (
        <>
          {!signedIn.user?.emailVerified && signedIn.user
            ? <VerifyBanner email={signedIn.user.email} /> : null}
          <ProfileScreen
            emailVerified={Boolean(signedIn.user?.emailVerified)}
            email={signedIn.user?.email}
            signInMethod={signedIn.signIn?.method}
            onSaved={() => void onProfileSaved()}
            onSignOut={() => void signOut()}
          />
        </>
      ) : (
        <Home session={signedIn} onOpenCampaign={(id) => navigate(`/campaigns/${id}`)} />
      )}
      </>
    );
  }
}

function HeaderAvatar() {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    void api.get<{ profile: { avatar_href?: string | null } | null }>("/onboarding/creator").then((r) => {
      if (r.ok) setSrc(r.data.profile?.avatar_href ?? null);
    });
  }, []);
  return <CreatorAvatar src={src} size={36} />;
}
